import React from 'react'
import PropTypes from 'prop-types'
import {
    Accordion,
    AccordionItem,
    AccordionButton,
    AccordionPanel,
    Box,
    Flex,
    Text
} from '@salesforce/retail-react-app/app/components/shared/ui'
import {MinusIcon, AddIcon} from '@chakra-ui/icons'
import {useIntl} from 'react-intl'
import {
    accordionHeadingStyle,
    accordionPanelStyle,
    iconStyle,
    informationAccordionStyle,
    styleIdContainer,
    styleIdText
} from './index.style'
import useFetchLibraryFolders from '../../../../hooks/useFetchLibrary'
import {useAccessToken} from '@salesforce/commerce-sdk-react'
import useCustomFetch from '../../../../hooks/useFetchContent'

const giftCardAssetIDs = ['cav-physicalgift-info', 'cav-physicalgift-terms']

const InformationAccordion = ({product}) => {
    const {formatMessage} = useIntl()
    const {token} = useAccessToken()
    const isGiftCard =
        product?.primaryCategoryId === 'gift-cards' ||
        product?.c_primaryCatergoryID === 'gift-cards'

    const {data: response} = useFetchLibraryFolders(
        isGiftCard ? null : 'content/pdp-shipping-returns'
    )

    const {response: giftInfoHtmlRaw} = useCustomFetch(
        isGiftCard ? token : null,
        giftCardAssetIDs[0],
        false
    )

    const {response: giftTermsHtmlRaw} = useCustomFetch(
        isGiftCard ? token : null,
        giftCardAssetIDs[1],
        false
    )

    // Helper to extract <h3> and body from HTML string
    const extractHeadingAndBody = (html) => {
        if (!html) return {heading: '', body: ''}

        const headingMatch = html.match(/<h3[^>]*>(.*?)<\/h3>/i)
        const heading = headingMatch ? headingMatch[1] : ''

        // Remove <h3>...</h3> from html to get body
        const body = html.replace(/<h3[^>]*>.*?<\/h3>/i, '').trim()

        return {heading, body}
    }

    const {heading: giftInfoHeading, body: giftInfoBody} = extractHeadingAndBody(giftInfoHtmlRaw)
    const {heading: giftTermsHeading, body: giftTermsBody} = extractHeadingAndBody(giftTermsHtmlRaw)

    return (
        <Box>
            {isGiftCard ? (
                <Accordion defaultIndex={[0]} allowMultiple>
                    {giftInfoHtmlRaw && (
                        <AccordionItem sx={informationAccordionStyle}>
                            {({isExpanded}) => (
                                <>
                                    <AccordionButton paddingBottom="0">
                                        <Flex w="100%" align="center" justify="space-between">
                                            <Text sx={accordionHeadingStyle}>
                                                {giftInfoHeading}
                                            </Text>
                                            <Box transform="translateY(-50%)">
                                                {isExpanded ? (
                                                    <MinusIcon sx={iconStyle} />
                                                ) : (
                                                    <AddIcon sx={iconStyle} />
                                                )}
                                            </Box>
                                        </Flex>
                                    </AccordionButton>
                                    <AccordionPanel sx={accordionPanelStyle}>
                                        <div dangerouslySetInnerHTML={{__html: giftInfoBody}} />
                                    </AccordionPanel>
                                </>
                            )}
                        </AccordionItem>
                    )}
                    {giftTermsHtmlRaw && (
                        <AccordionItem sx={informationAccordionStyle}>
                            {({isExpanded}) => (
                                <>
                                    <AccordionButton paddingBottom="0">
                                        <Flex w="100%" align="center" justify="space-between">
                                            <Text sx={accordionHeadingStyle}>
                                                {giftTermsHeading}
                                            </Text>
                                            <Box transform="translateY(-50%)">
                                                {isExpanded ? (
                                                    <MinusIcon sx={iconStyle} />
                                                ) : (
                                                    <AddIcon sx={iconStyle} />
                                                )}
                                            </Box>
                                        </Flex>
                                    </AccordionButton>
                                    <AccordionPanel sx={accordionPanelStyle}>
                                        <div dangerouslySetInnerHTML={{__html: giftTermsBody}} />
                                    </AccordionPanel>
                                </>
                            )}
                        </AccordionItem>
                    )}
                </Accordion>
            ) : (
                <Accordion defaultIndex={[0]} allowMultiple>
                    {/*Overview*/}
                    {product?.shortDescription && (
                        <AccordionItem sx={informationAccordionStyle}>
                            {({isExpanded}) => (
                                <>
                                    <AccordionButton paddingBottom="0">
                                        <Flex w="100%" align="center" justify="space-between">
                                            <Text sx={accordionHeadingStyle}>
                                                {formatMessage({
                                                    defaultMessage: 'Overview',
                                                    id: 'product_detail.accordion.button.overview'
                                                })}
                                            </Text>
                                            <Box transform="translateY(-50%)">
                                                {isExpanded ? (
                                                    <MinusIcon sx={iconStyle} />
                                                ) : (
                                                    <AddIcon sx={iconStyle} />
                                                )}
                                            </Box>
                                        </Flex>
                                    </AccordionButton>

                                    <AccordionPanel sx={accordionPanelStyle}>
                                        <div
                                            dangerouslySetInnerHTML={{
                                                __html: product?.shortDescription
                                            }}
                                        />
                                    </AccordionPanel>
                                </>
                            )}
                        </AccordionItem>
                    )}

                    {/* Details */}
                    {product?.longDescription && (
                        <AccordionItem sx={informationAccordionStyle}>
                            {({isExpanded}) => (
                                <>
                                    <AccordionButton paddingBottom="0">
                                        <Flex w="100%" align="center" justify="space-between">
                                            <Text sx={accordionHeadingStyle}>
                                                {formatMessage({
                                                    defaultMessage: 'Details',
                                                    id: 'product_detail.accordion.button.details'
                                                })}
                                            </Text>
                                            <Box transform="translateY(-50%)">
                                                {isExpanded ? (
                                                    <MinusIcon sx={iconStyle} />
                                                ) : (
                                                    <AddIcon sx={iconStyle} />
                                                )}
                                            </Box>
                                        </Flex>
                                    </AccordionButton>

                                    <AccordionPanel sx={accordionPanelStyle}>
                                        <div
                                            dangerouslySetInnerHTML={{
                                                __html: product?.longDescription
                                            }}
                                        />

                                        <Box sx={styleIdContainer}>
                                            <Text sx={styleIdText}>
                                                {`Style #${product?.id || ''}`}
                                            </Text>
                                        </Box>
                                    </AccordionPanel>
                                </>
                            )}
                        </AccordionItem>
                    )}

                    {/* Care Guide */}
                    {product?.c_careInstructions && (
                        <AccordionItem sx={informationAccordionStyle}>
                            {({isExpanded}) => (
                                <>
                                    <AccordionButton paddingBottom="0">
                                        <Flex w="100%" align="center" justify="space-between">
                                            <Text sx={accordionHeadingStyle}>
                                                {formatMessage({
                                                    defaultMessage: 'Care Guide',
                                                    id: 'product_detail.accordion.button.care_guide'
                                                })}
                                            </Text>
                                            <Box transform="translateY(-50%)">
                                                {isExpanded ? (
                                                    <MinusIcon sx={iconStyle} />
                                                ) : (
                                                    <AddIcon sx={iconStyle} />
                                                )}
                                            </Box>
                                        </Flex>
                                    </AccordionButton>
                                    <AccordionPanel sx={accordionPanelStyle}>
                                        <div
                                            dangerouslySetInnerHTML={{
                                                __html: product?.c_careInstructions
                                            }}
                                        />
                                    </AccordionPanel>
                                </>
                            )}
                        </AccordionItem>
                    )}

                    {/* Shipping & Returns */}
                    {response?.c_body && (
                        <AccordionItem sx={informationAccordionStyle}>
                            {({isExpanded}) => (
                                <>
                                    <AccordionButton paddingBottom="0">
                                        <Flex w="100%" align="center" justify="space-between">
                                            <Text sx={accordionHeadingStyle}>{response.name}</Text>
                                            <Box transform="translateY(-50%)">
                                                {isExpanded ? (
                                                    <MinusIcon sx={iconStyle} />
                                                ) : (
                                                    <AddIcon sx={iconStyle} />
                                                )}
                                            </Box>
                                        </Flex>
                                    </AccordionButton>
                                    <AccordionPanel sx={accordionPanelStyle}>
                                        <div dangerouslySetInnerHTML={{__html: response.c_body}} />
                                    </AccordionPanel>
                                </>
                            )}
                        </AccordionItem>
                    )}
                </Accordion>
            )}
        </Box>
    )
}

InformationAccordion.propTypes = {
    product: PropTypes.object
}

export default InformationAccordion
